import React from "react";
import { cn } from "@/lib/utils";

export interface TextProps {
  size?: "xs" | "sm" | "base" | "lg" | "xl";
  weight?: "normal" | "medium" | "semibold" | "bold";
  color?: "default" | "muted" | "primary" | "secondary" | "destructive";
  align?: "left" | "center" | "right" | "justify";
  as?: "span" | "p" | "div" | "strong" | "em" | "small" | "mark" | "del" | "ins";
  className?: string;
  children?: React.ReactNode;
}

const sizeClasses = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl",
};

const weightClasses = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

const colorClasses = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  primary: "text-primary",
  secondary: "text-secondary-foreground",
  destructive: "text-destructive",
};

const alignClasses = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
};

/**
 * Text component for inline text styling with consistent design tokens.
 *
 * @example
 * <Text size="sm" color="muted">Small muted text</Text>
 * <Text weight="bold" color="primary">Bold primary text</Text>
 * <Text as="div" align="center">Centered text block</Text>
 */
export function Text({
  size = "base",
  weight = "normal",
  color = "default",
  align = "left",
  as: Component = "span",
  className,
  children,
  ...props
}: TextProps & React.HTMLAttributes<HTMLElement>) {
  return (
    <Component
      className={cn(
        sizeClasses[size],
        weightClasses[weight],
        colorClasses[color],
        alignClasses[align],
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

// Convenience components for common text styles
export const TextMuted = (props: Omit<TextProps, "color"> & React.HTMLAttributes<HTMLElement>) => {
  return <Text {...props} color="muted" />;
};

export const TextSmall = (props: Omit<TextProps, "size"> & React.HTMLAttributes<HTMLElement>) => {
  return <Text {...props} size="sm" />;
};

export const TextLarge = (props: Omit<TextProps, "size"> & React.HTMLAttributes<HTMLElement>) => {
  return <Text {...props} size="lg" />;
};

export const TextBold = (props: Omit<TextProps, "weight"> & React.HTMLAttributes<HTMLElement>) => {
  return <Text {...props} weight="bold" />;
};
